import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiExt, isUnsupported } from '../lib/api';
import { t, useLocale } from '../lib/i18n';

type StepKey = 'tenant' | 'admin' | 'inbox' | 'seed' | 'snippet';
const STEPS: StepKey[] = ['tenant', 'admin', 'inbox', 'seed', 'snippet'];

interface Form {
  tenantCode: string;
  tenantName: string;
  adminEmail: string;
  inboxName: string;
}

export function OnboardingPage() {
  useLocale();
  const nav = useNavigate();
  const [stepIdx, setStepIdx] = useState(0);
  const [form, setForm] = useState<Form>({
    tenantCode: '',
    tenantName: '',
    adminEmail: '',
    inboxName: 'web-chat-default',
  });
  const [submitting, setSubmitting] = useState(false);
  const [unsupported, setUnsupported] = useState(false);
  const [created, setCreated] = useState<{ tenantCode: string; inboxId: string } | null>(null);

  const step = STEPS[stepIdx];

  const snippet = useMemo(() => {
    if (!created) return '';
    const base = window.location.origin.replace(/\/$/, '');
    return [
      `<script src="${base}/widget.js" async></script>`,
      `<script>`,
      `  window.ChatOlam = window.ChatOlam || [];`,
      `  window.ChatOlam.push(['init', {`,
      `    tenantCode: '${created.tenantCode}',`,
      `    inboxId: '${created.inboxId}'`,
      `  }]);`,
      `</script>`,
    ].join('\n');
  }, [created]);

  function canAdvance(): boolean {
    if (step === 'tenant') return form.tenantCode.trim().length > 0 && form.tenantName.trim().length > 0;
    if (step === 'admin') return /.+@.+\..+/.test(form.adminEmail);
    if (step === 'inbox') return form.inboxName.trim().length > 0;
    return true;
  }

  async function next() {
    if (step === 'inbox') {
      setSubmitting(true);
      try {
        const r = await apiExt.bootstrapTenant({
          tenantCode: form.tenantCode,
          tenantName: form.tenantName,
          adminEmail: form.adminEmail,
          inboxName: form.inboxName,
        });
        if (isUnsupported(r)) {
          setUnsupported(true);
          setCreated({ tenantCode: form.tenantCode, inboxId: 'pending' });
        } else {
          setCreated({ tenantCode: r.tenantCode, inboxId: r.inboxId });
        }
      } catch (e) {
        alert(`Bootstrap failed: ${e instanceof Error ? e.message : e}`);
        setSubmitting(false);
        return;
      } finally {
        setSubmitting(false);
      }
    }
    if (step === 'seed') {
      setSubmitting(true);
      try {
        const r = await apiExt.seedDefaults(form.tenantCode);
        if (isUnsupported(r)) setUnsupported(true);
      } catch {
        // graceful: continue regardless
      } finally {
        setSubmitting(false);
      }
    }
    setStepIdx((i) => Math.min(STEPS.length - 1, i + 1));
  }

  function back() {
    setStepIdx((i) => Math.max(0, i - 1));
  }

  function skip() {
    nav('/inbox');
  }

  function copySnippet() {
    try {
      void navigator.clipboard.writeText(snippet);
    } catch {
      // ignore
    }
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#f9fafb',
        padding: 24,
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <div style={{ maxWidth: 640, width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <h1 style={{ margin: 0, fontSize: 22 }}>{t('onboarding.title')}</h1>
          <button onClick={skip} style={btnGhost}>
            {t('common.skip')}
          </button>
        </div>

        <Stepper current={stepIdx} />

        <div
          style={{
            background: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: 8,
            padding: 24,
            marginTop: 16,
          }}
        >
          {unsupported && (
            <div
              style={{
                background: '#fef3c7',
                border: '1px solid #fde68a',
                borderRadius: 6,
                padding: 8,
                marginBottom: 12,
                fontSize: 13,
                color: '#92400e',
              }}
            >
              {t('settings.security.backendMissing')}
            </div>
          )}

          {step === 'tenant' && (
            <>
              <Field
                label={t('onboarding.tenant.code')}
                value={form.tenantCode}
                onChange={(v) => setForm({ ...form, tenantCode: v.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                placeholder="my-tenant"
              />
              <Field
                label={t('onboarding.tenant.name')}
                value={form.tenantName}
                onChange={(v) => setForm({ ...form, tenantName: v })}
                placeholder="My Tenant"
              />
            </>
          )}

          {step === 'admin' && (
            <Field
              label={t('onboarding.admin.email')}
              value={form.adminEmail}
              onChange={(v) => setForm({ ...form, adminEmail: v })}
              placeholder="admin@example.com"
            />
          )}

          {step === 'inbox' && (
            <Field
              label={t('onboarding.inbox.name')}
              value={form.inboxName}
              onChange={(v) => setForm({ ...form, inboxName: v })}
            />
          )}

          {step === 'seed' && (
            <div>
              <p style={{ fontSize: 14, color: '#374151' }}>{t('onboarding.seed.cta')}</p>
            </div>
          )}

          {step === 'snippet' && (
            <div>
              <label style={{ display: 'block', fontSize: 13, color: '#6b7280', marginBottom: 6 }}>
                {t('onboarding.snippet.title')}
              </label>
              <pre
                style={{
                  background: '#0f172a',
                  color: '#e2e8f0',
                  padding: 12,
                  borderRadius: 6,
                  fontSize: 12,
                  overflowX: 'auto',
                }}
              >
                {snippet}
              </pre>
              <button onClick={copySnippet} style={{ ...btnSecondary, marginTop: 8 }}>
                {t('onboarding.snippet.copy')}
              </button>
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            <button onClick={back} disabled={stepIdx === 0} style={btnSecondary}>
              {t('common.previous')}
            </button>
            {stepIdx < STEPS.length - 1 ? (
              <button onClick={next} disabled={!canAdvance() || submitting} style={btnPrimary}>
                {submitting ? t('common.loading') : t('common.next')}
              </button>
            ) : (
              <Link to="/inbox" style={{ ...btnPrimary, textDecoration: 'none', display: 'inline-block' }}>
                {t('onboarding.done')}
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Stepper({ current }: { current: number }) {
  return (
    <div style={{ display: 'flex', gap: 4 }}>
      {STEPS.map((s, i) => (
        <div
          key={s}
          style={{
            flex: 1,
            height: 6,
            background: i <= current ? '#2563eb' : '#e5e7eb',
            borderRadius: 3,
          }}
          title={t(`onboarding.step.${s}`)}
        />
      ))}
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ display: 'block', fontSize: 12, color: '#6b7280', marginBottom: 4 }}>{label}</label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        style={{
          width: '100%',
          padding: '8px 10px',
          fontSize: 14,
          border: '1px solid #d1d5db',
          borderRadius: 6,
          boxSizing: 'border-box',
        }}
      />
    </div>
  );
}

const btnPrimary: React.CSSProperties = {
  padding: '8px 16px',
  background: '#2563eb',
  color: 'white',
  border: 'none',
  borderRadius: 6,
  cursor: 'pointer',
  fontSize: 14,
};
const btnSecondary: React.CSSProperties = {
  padding: '8px 16px',
  background: 'white',
  color: '#374151',
  border: '1px solid #d1d5db',
  borderRadius: 6,
  cursor: 'pointer',
  fontSize: 14,
};
const btnGhost: React.CSSProperties = {
  padding: '6px 10px',
  background: 'transparent',
  color: '#6b7280',
  border: 'none',
  cursor: 'pointer',
  fontSize: 13,
};
